import { useEffect } from "react";
import { useRouter } from "next/router";

export type UTMParams = "utm_source" | "utm_medium" | "utm_campaign" | "utm_term" | "gclid" | "fbclid";
export const utmTags = ["utm_source", "utm_medium", "utm_campaign", "utm_term", "gclid", "fbclid"];

function mapReferrerToSource(referrer: string): string {
  const ref = referrer.toLowerCase();
  if (ref.includes("facebook.com") || ref.includes("fb.com")) return "facebook";
  if (ref.includes("instagram.com")) return "instagram";
  if (ref.includes("twitter.com") || ref.includes("t.co") || ref.includes("x.com"))
    return "twitter";
  if (ref.includes("linkedin.com")) return "linkedin";
  if (ref.includes("youtube.com")) return "youtube";
  if (ref.includes("google.")) return "google";
  if (ref.includes("bing.com")) return "bing";
  if (ref.includes("yahoo.com")) return "yahoo";
  if (ref.includes("reddit.com")) return "reddit";

  try {
    const url = new URL(referrer);
    return url.hostname.replace("www.", "");
  } catch {
    return referrer;
  }
}

export function useAttributionTracker(): void {
  const router = useRouter();

  useEffect(() => {
    if (!router.isReady) return;

    utmTags.forEach((tag) => {
      const existing = localStorage.getItem(tag);

      if (typeof router.query[tag] === "string") {
        let value = (router.query[tag] as string).trim();

        // Lowercase only UTMs
        if (tag !== "gclid" && tag !== "fbclid") {
          value = value.toLowerCase();
        }

        if (existing !== value) {
          localStorage.setItem(tag, value);
        }
      } else if (tag === "utm_source" && !existing) {
        const refValue = document.referrer
          ? mapReferrerToSource(document.referrer).trim().toLowerCase()
          : "direct";
        localStorage.setItem(tag, refValue);
      } else if (tag !== "gclid" && !existing) {
        localStorage.setItem(tag, "unknown");
      }
    });
  }, [router.isReady, router.query]);
}

export function getStoredUTMParam(param: UTMParams): string {
  if (typeof window === "undefined") return "unknown";
  return localStorage.getItem(param) || "unknown";
}

export function getAllStoredUTMParams(): string {
  return utmTags
    .map((param) => {
      const value = getStoredUTMParam(param as UTMParams);
      return value !== "unknown" ? `${param}=${encodeURIComponent(value)}` : null;
    })
    .filter(Boolean)
    .join("&");
}
